import { useEffect, useRef, useState, useCallback, useMemo } from 'react';
import { PRICING_SECTIONS, SECTION_DOT_COLOR } from '../constants/pricingSections';
import useActiveSection from '../hooks/useActiveSection';

/**
 * Barra de progreso superior fija con mini-marcadores tipo YouTube.
 *
 * - Muestra el % de lectura en tiempo real.
 * - Click en track: salta suavemente a esa fraccion del documento.
 * - Drag (mouse + touch): navegacion fluida tipo timeline.
 * - Mini-marcadores: tick blanco fino + dot pequeño coloreado por seccion.
 *   Hover en marcador muestra tooltip con el label.
 *   Click en marcador: scroll-suave a esa seccion (data-testid).
 */
export default function PricingProgressBar({ language = 'es' }) {
  const [progress, setProgress] = useState(0);
  const [markers, setMarkers] = useState([]); // [{ id, label, color, percent }]
  const trackRef = useRef(null);
  const draggingRef = useRef(false);

  // Seccion activa visible para destacar el marcador correspondiente.
  const sectionIds = useMemo(() => PRICING_SECTIONS.map((s) => s.id), []);
  const activeId = useActiveSection(sectionIds);

  // Recalcula el progreso de lectura como fraccion del DOCUMENTO ya visto
  // (no del scroll restante). Esto da el comportamiento natural tipo lectura:
  //   100% = el final del documento esta en el borde inferior del viewport.
  useEffect(() => {
    const update = () => {
      const docH = document.documentElement.scrollHeight;
      if (docH <= 0) {
        setProgress(0);
        return;
      }
      const seen = window.scrollY + window.innerHeight;
      const ratio = Math.min(1, Math.max(0, seen / docH));
      setProgress(ratio);
    };
    update();
    window.addEventListener('scroll', update, { passive: true });
    window.addEventListener('resize', update);
    return () => {
      window.removeEventListener('scroll', update);
      window.removeEventListener('resize', update);
    };
  }, []);

  // Recalcula la posicion (% del documento) de cada seccion al cargar y al
  // cambiar el viewport (en resize las offsetTop pueden moverse mucho).
  const computeMarkers = useCallback(() => {
    const docH = document.documentElement.scrollHeight;
    if (docH <= 0) {
      setMarkers([]);
      return;
    }
    const list = PRICING_SECTIONS
      .map((s) => {
        const el = document.querySelector(`[data-testid="${s.id}"]`);
        if (!el) return null;
        const rect = el.getBoundingClientRect();
        const offset = rect.top + window.scrollY;
        // Posicion del marcador como fraccion del DOCUMENTO completo
        // (mismo sistema que el progreso de lectura).
        const percent = Math.min(99, Math.max(1, (offset / docH) * 100));
        return {
          id: s.id,
          label: s.label[language] || s.label.es,
          color: s.color,
          percent,
        };
      })
      .filter(Boolean);
    setMarkers(list);
  }, [language]);

  useEffect(() => {
    // Recalcular varias veces hasta que el layout se estabilice (imagenes,
    // fuentes, lazy-rendered sections, banner de cookies). Sin esto, los
    // marcadores cercanos al final pueden colapsar a 99.5%.
    const timers = [50, 250, 600, 1200, 2000, 3500].map((ms) =>
      setTimeout(computeMarkers, ms)
    );
    // Debounced recompute para resize y mutaciones de DOM frecuentes.
    let debounceTimer = null;
    const debouncedRecompute = () => {
      clearTimeout(debounceTimer);
      debounceTimer = setTimeout(computeMarkers, 120);
    };
    window.addEventListener('resize', debouncedRecompute);
    const observer = new MutationObserver(debouncedRecompute);
    observer.observe(document.body, { childList: true, subtree: true });
    return () => {
      timers.forEach(clearTimeout);
      clearTimeout(debounceTimer);
      window.removeEventListener('resize', debouncedRecompute);
      observer.disconnect();
    };
  }, [computeMarkers]);

  // ---- Helpers ----
  const fractionFromEvent = (e) => {
    const track = trackRef.current;
    if (!track) return 0;
    const rect = track.getBoundingClientRect();
    const clientX = e.touches?.[0]?.clientX ?? e.clientX;
    if (clientX == null) return 0;
    const x = clientX - rect.left;
    return Math.min(1, Math.max(0, x / rect.width));
  };

  const scrollToFraction = (fraction, smooth) => {
    // fraction es la posicion deseada en el DOCUMENTO completo (0..1).
    // Convertimos a scrollY destino. Si fraction*docH < innerHeight, el
    // top del documento ya esta visto; dejamos scrollY en 0.
    const docH = document.documentElement.scrollHeight;
    const target = Math.max(0, fraction * docH - window.innerHeight);
    window.scrollTo({ top: target, behavior: smooth ? 'smooth' : 'auto' });
  };

  const jumpToSection = (sectionId) => {
    const el = document.querySelector(`[data-testid="${sectionId}"]`);
    if (el) el.scrollIntoView({ behavior: 'smooth', block: 'start' });
  };

  // ---- Mouse drag ----
  const handleMouseDown = (e) => {
    draggingRef.current = true;
    scrollToFraction(fractionFromEvent(e), false);
    e.preventDefault();
  };
  useEffect(() => {
    const onMove = (e) => {
      if (!draggingRef.current) return;
      scrollToFraction(fractionFromEvent(e), false);
    };
    const onUp = () => { draggingRef.current = false; };
    window.addEventListener('mousemove', onMove);
    window.addEventListener('mouseup', onUp);
    return () => {
      window.removeEventListener('mousemove', onMove);
      window.removeEventListener('mouseup', onUp);
    };
  }, []);

  // ---- Touch drag ----
  const handleTouchStart = (e) => {
    draggingRef.current = true;
    scrollToFraction(fractionFromEvent(e), false);
  };
  const handleTouchMove = (e) => {
    if (!draggingRef.current) return;
    scrollToFraction(fractionFromEvent(e), false);
  };
  const handleTouchEnd = () => { draggingRef.current = false; };

  // ---- Click directo en track (no en marcador) ----
  const handleClick = (e) => {
    if (draggingRef.current) return; // ya manejado por mousedown
    scrollToFraction(fractionFromEvent(e), true);
  };

  const pct = Math.round(progress * 100);

  return (
    <div
      ref={trackRef}
      onClick={handleClick}
      onMouseDown={handleMouseDown}
      onTouchStart={handleTouchStart}
      onTouchMove={handleTouchMove}
      onTouchEnd={handleTouchEnd}
      className="fixed top-0 left-0 right-0 h-[3px] bg-slate-800/60 z-[60] cursor-pointer
                 group select-none touch-none"
      role="progressbar"
      aria-label="Reading progress"
      aria-valuenow={pct}
      aria-valuemin={0}
      aria-valuemax={100}
      data-testid="pricing-progress-bar"
    >
      {/* Hit-area mas grande para click facil sin engrosar la linea */}
      <div className="absolute inset-x-0 -top-1 h-3" aria-hidden="true" />

      {/* Fill */}
      <div
        className="h-full bg-gradient-to-r from-cyan-400 via-amber-400 to-emerald-400
                   transition-[width] duration-150 ease-out
                   shadow-[0_0_8px_rgba(16,185,129,0.6)]"
        style={{ width: `${pct}%` }}
        data-testid="pricing-progress-bar-fill"
      />

      {/* Mini-marcadores tipo YouTube */}
      {markers.map((m) => {
        const dotClass = SECTION_DOT_COLOR[m.color] || 'bg-white';
        const isActive = activeId === m.id;
        return (
          <button
            key={m.id}
            type="button"
            onMouseDown={(e) => e.stopPropagation()}
            onTouchStart={(e) => e.stopPropagation()}
            onClick={(e) => {
              e.stopPropagation();
              jumpToSection(m.id);
            }}
            title={m.label}
            aria-label={`${language === 'es' ? 'Ir a' : 'Go to'} ${m.label}`}
            aria-current={isActive ? 'true' : undefined}
            className="absolute top-0 -translate-x-1/2 h-[3px] flex flex-col items-center
                       cursor-pointer pointer-events-auto"
            style={{ left: `${m.percent}%` }}
            data-testid={`pricing-progress-marker-${m.id}`}
          >
            {/* Tick blanco que separa visualmente. Crece y se vuelve emerald al activarse. */}
            <span
              className={`transition-all duration-200
                          ${isActive
                            ? 'w-[3px] h-[6px] bg-emerald-400 marker-active'
                            : 'w-[2px] h-[3px] bg-white/85'}`}
              aria-hidden="true"
            />
            {/* Dot debajo de la barra (coloreado por seccion). */}
            <span
              className={`mt-[3px] block rounded-full transition-transform duration-200
                          ring-2 ring-slate-900 shadow-md
                          ${isActive
                            ? `w-[10px] h-[10px] ${dotClass} scale-110`
                            : `w-[6px] h-[6px] ${dotClass} group-hover:scale-125`}
                          hover:!scale-150`}
              aria-hidden="true"
            />
            {/* Tooltip con label en hover (desktop) */}
            <span
              className="hidden lg:group-hover:block absolute top-3 left-1/2 -translate-x-1/2
                         mt-2 px-2 py-0.5 rounded bg-slate-900/95 border border-slate-700/60
                         text-[10px] text-slate-200 whitespace-nowrap pointer-events-none
                         opacity-0 group-hover:opacity-100 transition-opacity duration-150"
            >
              {m.label}
            </span>
          </button>
        );
      })}

      {/* Knob actual visible al hover/drag */}
      <div
        className="absolute top-1/2 -translate-y-1/2 w-3 h-3 rounded-full bg-emerald-300
                   border-2 border-slate-900 shadow-md
                   opacity-0 group-hover:opacity-100 transition-opacity duration-200
                   pointer-events-none"
        style={{ left: `calc(${pct}% - 6px)` }}
      />
    </div>
  );
}
